import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { useLiveExactLimits } from '../../hooks/useLiveExactLimits';
import { ExactUsage, getExactUsage } from '../data/cockpit';
import { envKind, exactMeter, exactTone, useCockpit } from '../data/CockpitContext';
import { clock, number, until } from '../data/format';
import { usePoll } from '../data/usePoll';
import { Card, CheckRow, Empty, EnvChip, ErrorNotice, Kv, Loading, Meter } from '../components/ui';
import { v2 } from '../paths';
import { useBarTip } from '../components/BarTip';

const SOURCES: { key: string; label: string; color: string; match: (s: string) => boolean }[] = [
    { key: 'connector', label: 'Connector', color: '#3E86B5', match: () => false },
    { key: 'sweep', label: 'Scanner', color: '#B49CFF', match: (s) => s === 'sweep' },
    { key: 'reevaluation', label: 'Herbeoordelen', color: '#F28DC0', match: (s) => s === 'reevaluation' },
    { key: 'rolloutwatch', label: 'Monitor', color: '#FFC861', match: (s) => s.startsWith('rolloutwatch') },
    { key: 'liveread', label: 'Live lezen', color: '#5EEAD4', match: (s) => s === 'liveread' },
    { key: 'other', label: 'Scherm en overig', color: '#8A94A3', match: (s) => s === 'screen' || s === 'other' },
];

const perSource = (dashboard?: Record<string, number> | null) => {
    const totals: Record<string, number> = {};
    Object.entries(dashboard ?? {}).forEach(([source, n]) => {
        const s = SOURCES.find((x) => x.match(source)) ?? SOURCES[SOURCES.length - 1];
        totals[s.key] = (totals[s.key] ?? 0) + n;
    });
    return totals;
};

const Detail = ({ customer, environment, inEnv }: { customer: string; environment: string; inEnv?: boolean }) => {
    const axios = useAxiosSwagger();
    const { find } = useCockpit();
    const env = find(customer, environment);
    const limits = useLiveExactLimits(customer, environment);
    const usage = usePoll<ExactUsage>(axios ? (s) => getExactUsage(axios, customer, environment, 24, s) : null, 60000, [axios, customer, environment], `exact-usage/${customer}/${environment}`);

    const hours = useMemo(
        () =>
            (usage.data?.hours ?? []).map((h) => {
                const s = perSource(h.dashboard);
                s.connector = h.connectorInferred ?? h.connectorLogged ?? 0;
                return { hour: h.hour, sources: s, total: Object.values(s).reduce((a, b) => a + b, 0), throttled: h.throttled };
            }),
        [usage.data]
    );
    const max = Math.max(...hours.map((h) => h.total), 1);
    const tip = useBarTip();
    const sums = SOURCES.map((s) => ({ ...s, n: hours.reduce((a, h) => a + (h.sources[s.key] ?? 0), 0) }));

    const remaining = limits?.dailyRemaining ?? usage.data?.remaining ?? env?.exactRemaining;
    const limit = limits?.dailyLimit ?? usage.data?.limit ?? env?.exactLimit;
    const runsOut = usage.data?.runsOutAt ?? env?.exactRunsOutAt;

    return (
        <div className="stack">
            <Card
                title={
                    inEnv ? (
                        'Calls per uur · 24u'
                    ) : (
                        <div className="h2 hrow" style={{ gap: 8 }}>
                            {customer} <EnvChip kind={env ? envKind(env) : 'prod'} environment={environment} />
                            <Link className="btn sm ghost" to={v2.envExact(customer, environment)}>Naar de omgeving</Link>
                        </div>
                    )
                }
                right={<span className={runsOut ? 'chip bad' : 'chip ok'}>{runsOut ? `op om ${clock(runsOut)}` : 'haalt het einde van de dag'}</span>}
            >
                <div className="cb">
                    <ErrorNotice error={usage.error} />
                    {usage.pending && <Loading>Verbruik per uur ophalen…</Loading>}
                    <div className="hrow" style={{ gap: 14, fontSize: 12, marginBottom: 8 }}>
                        {SOURCES.map((s) => (
                            <span key={s.key} className="mut">
                                <span style={{ color: s.color }}>■</span> {s.label}
                            </span>
                        ))}
                        <span className="mut"><span className="bad">▲</span> 429</span>
                    </div>
                    <div className="tiphost" ref={tip.host}>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 170 }}>
                            {hours.map((h) => (
                                <div
                                    key={h.hour}
                                    {...tip.bind({
                                        title: `${clock(h.hour)}–${clock(new Date(new Date(h.hour).getTime() + 3600000).toISOString())} · ${number(h.total)} calls`,
                                        rows: SOURCES.filter((s) => (h.sources[s.key] ?? 0) > 0).map((s) => ({ label: s.label, value: number(h.sources[s.key]), color: s.color })),
                                        note: h.throttled > 0 ? <span className="bad">{number(h.throttled)}× 429 (limiet)</span> : h.total === 0 ? 'geen calls' : undefined,
                                    })}
                                    style={{ flex: 1, display: 'flex', flexDirection: 'column-reverse', height: '100%', position: 'relative' }}
                                >
                                    {SOURCES.map((s) => (
                                        <i key={s.key} style={{ display: 'block', height: `${((h.sources[s.key] ?? 0) / max) * 100}%`, background: s.color }} />
                                    ))}
                                    {h.throttled > 0 && <span className="bad" style={{ position: 'absolute', top: -2, left: '50%', transform: 'translateX(-50%)', fontSize: 10 }}>▲</span>}
                                </div>
                            ))}
                        </div>
                        {tip.layer}
                    </div>
                    <div className="hrow mono mut" style={{ justifyContent: 'space-between', fontSize: 11, marginTop: 6 }}>
                        <span>{hours[0] ? clock(hours[0].hour) : ''}</span>
                        <span>{hours.length ? clock(hours[hours.length - 1].hour) : ''}</span>
                    </div>
                </div>
            </Card>
            <div className="g2e">
                <Card title="Vandaag">
                    <div className="cb">
                        <Kv
                            rows={[
                                ['Over', <span className="mono">{number(remaining)} van {number(limit)}</span>],
                                ['Laatste uur', <span className="mono">{number(usage.data?.usedLastHour ?? env?.exactUsedLastHour)}</span>],
                                ['Op om', <span className={runsOut ? 'bad' : ''}>{runsOut ? clock(runsOut) : '–'}</span>],
                                ['Minuut', limits?.minutelyRemaining !== undefined && limits?.minutelyRemaining !== null ? `${limits.minutelyRemaining} van ${limits.minutelyLimit ?? 60}` : '–'],
                            ]}
                        />
                    </div>
                </Card>
                <Card title="Wie gebruikte het (24u)">
                    <div className="cb checks">
                        {sums.map((s) => (
                            <div key={s.key} className="chk" style={{ gridTemplateColumns: '12px minmax(0,1fr) auto' }}>
                                <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color }} />
                                <span>{s.label}</span>
                                <span className="v">{usage.data ? number(s.n) : '…'}</span>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
            {usage.data?.connectorError && <CheckRow tone="warn" label="Connectorlog niet gelezen" value={usage.data.connectorError} />}
        </div>
    );
};

/** The Exact tab of one environment, inside its frame. */
export const EnvExactPage = () => {
    const { customer = '', environment = '' } = useParams();
    return <Detail customer={customer} environment={environment} inEnv />;
};

export const ExactPage = () => {
    const { customer, environment } = useParams();
    const { shown: environments, loadedAt } = useCockpit();
    const sorted = [...environments].filter((e) => e.exactRemaining !== null && e.exactRemaining !== undefined).sort((a, b) => (a.exactRemaining ?? 0) - (b.exactRemaining ?? 0));
    const selected = customer && environment ? { customer, environment } : sorted[0];

    return (
        <>
            <div className="pagehead">
                <div>
                    <div className="eyebrow">Daglimiet per omgeving · reset 00:00 UTC</div>
                    <h1 className="h1">Exact-verbruik</h1>
                </div>
            </div>
            <div className="g2" style={{ gridTemplateColumns: 'minmax(300px, 0.8fr) minmax(0, 1.6fr)' }}>
                <Card title="Minst over eerst">
                    {!loadedAt && <Loading />}
                    {loadedAt && sorted.length === 0 && <Empty>Nog geen limieten gelezen.</Empty>}
                    {sorted.map((e) => {
                        const used = e.exactLimit ? ((e.exactLimit - (e.exactRemaining ?? 0)) / e.exactLimit) * 100 : 0;
                        const on = selected && selected.customer === e.customer && selected.environment === e.environment;
                        return (
                            <Link key={`${e.customer}/${e.environment}`} className={`li ${on ? 'on' : ''}`} style={{ gridTemplateColumns: 'minmax(0,1fr) 110px 64px' }} to={v2.exact(e.customer, e.environment)}>
                                <span>
                                    <b style={{ fontWeight: 600 }}>{e.customer}</b> <EnvChip kind={envKind(e)} environment={e.environment} />
                                    <span className="m">{e.exactRunsOutAt ? <span className="bad" title={clock(e.exactRunsOutAt)}>op over {until(e.exactRunsOutAt)}</span> : e.exactLimit ? `${number(e.exactLimit - (e.exactRemaining ?? 0))} gebruikt` : ''}</span>
                                </span>
                                <Meter pct={used} tone={exactMeter(exactTone(e, used))} width="100%" />
                                <span className="mono" style={{ textAlign: 'right' }}>{number(e.exactRemaining)}</span>
                            </Link>
                        );
                    })}
                </Card>
                {selected ? <Detail customer={selected.customer} environment={selected.environment} /> : <Empty>Kies een omgeving.</Empty>}
            </div>
        </>
    );
};
